import { IInteractive } from "../interfaces/IInteractive";
import { TextStatesData, TextureData, TextureStatesData } from "../types/types";


/**
 * Interactive, Toggleable, .. eg 'state dependent' .. view defs
 */
export type UpdateViewDef = {
    view: Phaser.GameObjects.Image | Phaser.GameObjects.Text;
    textureStates?: TextureStatesData,
    /**
     * Toggle buttons have their extra out/disabled toggle texture states (over unused)
     */
    textureToggleStates?: TextureStatesData,

    textStates?: TextStatesData,
    textToggleStates?: TextStatesData,
}

export enum GameObjectType {
    IMAGE = "Image",
    TEXT = "Text",
}


/**
 * UpdateView is necessary for Mixin bases that use Interactive and Toggleable Mixins
 * It let's their views react to state changes caused by those Mixins.
 * 
 * Aggregates state changes
 * Reacts to cumulative state change (multiple state resolution) 
 * by appropriately changing view (MISO)
 * 
 * NOTE: Toggleable: if using 
 * 1) single button with click=toggle:on/off functionality, never disable the button 
 *   (toggle on/off both clickable). SET VIEWPROPS ACCORDINGLY
 * 2) ToggleGroup to manage toggles: toggle:on/selected EQUALS DISABLED.
 *    SET VIEWPROPS ACCORDINGLY
 *       
 */
export class UpdateView
    extends Phaser.GameObjects.Container
{
    protected updateViews: UpdateViewDef[];
    /**
     * Is mouse pointer over this interactable
     */
    protected pointerOver: boolean;


    public AddViewDef(def: UpdateViewDef) {
        if (this.updateViews == undefined) {
            this.updateViews = [];
        }
        this.updateViews.push(def);
    }

    /**
     * Add internal listeners
     * Call this manually in the class where you extends the Mixin
     */
    public AddListeners(): void
    {
        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, this.OnPointerOver, this);
        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, this.OnPointerOut, this);
    }
    public RemoveListeners(): void
    {
        this.off(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, this.OnPointerOver);
        this.off(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, this.OnPointerOut);
    }
    
    protected OnPointerOver(pointer: Phaser.Input.Pointer, localX:number, localY:number, event:Phaser.Types.Input.EventData)
    {
        // console.log("UpdateView::OnPointerOver");

        this.pointerOver = true;
        this.UpdateView();
    }
    
    protected OnPointerOut(pointer:Phaser.Input.Pointer, event:Phaser.Types.Input.EventData)
    {
        // console.log("ButtonBase::OnPointerOut");

        this.pointerOver = false;
        this.UpdateView();
    }

    /**
     * State or part of state changed
     * Update view
     */
    public UpdateViewFromSelected(newSelected:boolean): void
    {
        this.UpdateView();
    }
    
    public UpdateViewFromInteractive(newInteractive:boolean): void
    {
        // console.log("UpdateViewFromInteractive", newInteractive);

        this.UpdateView();
    }

    public UpdateView(): void
    {
        // Interactive
        // @ts-ignore
        const hasInteractive: boolean = this.IsInteractive != undefined;
        // @ts-ignore
        const isInteractive: boolean = hasInteractive ? this.IsInteractive() : true;
        // console.log(hasInteractive, isInteractive);

        // Toggleable
        // @ts-ignore
        const hasSelected: boolean = this.IsSelected != undefined;
        // @ts-ignore
        const isSelected: boolean = hasSelected ? this.IsSelected() : false;
        // console.log(hasSelected, isSelected);

        // 
        const updateViews: UpdateViewDef[] = this.updateViews;
        let targetTextureData: TextureData;
        let targetTextData: Phaser.Types.GameObjects.Text.TextStyle;
        // enabled
        if (isInteractive) {
            // uses toggle (ON|OFF)
            if (hasSelected) {
                // selected (toggle ON)
                if (isSelected) {
                    //#region enabled, uses toggle - selected
                    updateViews.forEach(viewDef=>{
                        switch(viewDef.view.type) {
                            case GameObjectType.IMAGE:
                                targetTextureData = this.pointerOver ?
                                    (viewDef.textureToggleStates?.over || viewDef.textureToggleStates?.out) :
                                    viewDef.textureToggleStates?.out;
                                if (targetTextureData) {
                                    const view = viewDef.view as Phaser.GameObjects.Image;
                                    // if this is changing your origin, check TexturePacker sprite origin settings;
                                    view.setTexture(targetTextureData.texture, targetTextureData.frame);
                                }
                                break;
                            case GameObjectType.TEXT:
                                targetTextData = this.pointerOver ?
                                    (viewDef.textToggleStates?.over || viewDef.textToggleStates?.out) :
                                    viewDef.textToggleStates?.out;
                                if (targetTextData) {
                                    const view = viewDef.view as Phaser.GameObjects.Text;
                                    if (targetTextData.metrics) {
                                        view.style.setStyle(targetTextData, false, false);
                                        view.style["metrics"] = targetTextData.metrics;
                                        view.updateText();
                                    }
                                    else {
                                        view.setStyle(targetTextData);
                                    }
                                }
                                break;
                            default:
                                console.warn("UpdateView; Unrecognized view type:", viewDef.view.type);
                        }
                    });
                    //#endregion
                }
                // deselected (toggle OFF)
                else {
                    //#region enabled, uses toggle - deselected
                    updateViews.forEach(viewDef=>{
                        switch(viewDef.view.type) {
                            case GameObjectType.IMAGE:
                                targetTextureData = this.pointerOver ?
                                (viewDef.textureStates?.over || viewDef.textureStates?.out) :
                                viewDef.textureStates?.out;
                                if (targetTextureData) {
                                    const view = viewDef.view as Phaser.GameObjects.Image;
                                    view.setTexture(targetTextureData.texture, targetTextureData.frame);
                                }
                                break;
                            case GameObjectType.TEXT:
                                targetTextData = this.pointerOver ?
                                    (viewDef.textStates?.over || viewDef.textStates?.out) :
                                    viewDef.textStates?.out;
                                if (targetTextData) {
                                    const view = viewDef.view as Phaser.GameObjects.Text;
                                    if (targetTextData) {
                                        if (targetTextData.metrics) {
                                            view.style.setStyle(targetTextData, false, false);
                                            view.style["metrics"] = targetTextData.metrics;
                                            view.updateText();
                                        }
                                        else {
                                            view.setStyle(targetTextData);
                                        }
                                    }
                                }
                                break;
                            default:
                                console.warn("UpdateView; Unrecognized view type:", viewDef.view.type);
                        }
                    });
                    //#endregion
                }
            }
            // doesnt use toggle (ON|OFF)
            else {
                //#region enabled, doesnt use toggle
                updateViews.forEach(viewDef=>{
                    switch(viewDef.view.type) {
                        case GameObjectType.IMAGE:
                            targetTextureData = this.pointerOver ?
                                viewDef.textureStates?.over : 
                                viewDef.textureStates?.out;
                            if (targetTextureData) {
                                const view = viewDef.view as Phaser.GameObjects.Image;
                                view.setTexture(targetTextureData.texture, targetTextureData.frame);
                            }
                            break;
                        case GameObjectType.TEXT:
                            targetTextData = this.pointerOver ?
                                viewDef.textStates?.over : 
                                viewDef.textStates?.out;
                            if (targetTextData) {
                                const view = viewDef.view as Phaser.GameObjects.Text;
                                if (targetTextData) {
                                    if (targetTextData.metrics) {
                                        view.style.setStyle(targetTextData, false, false);
                                        view.style["metrics"] = targetTextData.metrics;
                                        view.updateText();
                                    }
                                    else {
                                        view.setStyle(targetTextData);
                                    }
                                }
                            }
                            break;
                        default:
                            console.warn("UpdateView; Unrecognized view type:", viewDef.view.type);
                    }
                });
                //#endregion
            }
        }
        // disabled
        else {
            // uses toggle (ON|OFF)
            if (hasSelected) {
                // selected (toggle ON)
                if (isSelected) {
                    //#region disabled, uses toggle - selected
                    updateViews.forEach(viewDef=>{
                        switch(viewDef.view.type) {
                            case GameObjectType.IMAGE:
                                targetTextureData = 
                                    viewDef.textureToggleStates?.disabled || 
                                    viewDef.textureToggleStates?.out;
                                if (targetTextureData) {
                                    const view = viewDef.view as Phaser.GameObjects.Image;
                                    view.setTexture(targetTextureData.texture, targetTextureData.frame);
                                }
                                break;
                            case GameObjectType.TEXT:
                                targetTextData = 
                                    viewDef.textToggleStates?.disabled || 
                                    viewDef.textToggleStates?.out;
                                if (targetTextData) {
                                    const view = viewDef.view as Phaser.GameObjects.Text;
                                    if (targetTextData.metrics) {
                                        view.style.setStyle(targetTextData, false, false);
                                        view.style["metrics"] = targetTextData.metrics;
                                        view.updateText();
                                    }
                                    else {
                                        view.setStyle(targetTextData);
                                    }
                                }
                                break;
                            default:
                                console.warn("UpdateView; Unrecognized view type:", viewDef.view.type);
                        }
                    });
                    //#endregion
                }
                // deselected (toggle OFF)
                else {
                    //#region disabled, uses toggle - deselected
                    updateViews.forEach(viewDef=>{
                        switch(viewDef.view.type) {
                            case GameObjectType.IMAGE:
                                targetTextureData = 
                                    viewDef.textureStates?.disabled ||
                                    viewDef.textureStates?.out;
                                if (targetTextureData) {
                                    const view = viewDef.view as Phaser.GameObjects.Image;
                                    view.setTexture(targetTextureData.texture, targetTextureData.frame);
                                }
                                break;
                            case GameObjectType.TEXT:
                                targetTextData = 
                                    viewDef.textStates?.disabled ||
                                    viewDef.textStates?.out;
                                if (targetTextData) {
                                    const view = viewDef.view as Phaser.GameObjects.Text;
                                    if (targetTextData.metrics) {
                                        view.style.setStyle(targetTextData, false, false);
                                        view.style["metrics"] = targetTextData.metrics;
                                        view.updateText();
                                    }
                                    else {
                                        view.setStyle(targetTextData);
                                    }
                                }
                                break;
                            default:
                                console.warn("UpdateView; Unrecognized view type:", viewDef.view.type);
                        }
                    });
                    //#endregion
                }
            }
            // doesnt use toggle (ON|OFF)
            else {
                //#region disabled, doesnt use toggle
                updateViews.forEach(viewDef=>{
                    switch(viewDef.view.type) {
                        case GameObjectType.IMAGE:
                            targetTextureData = 
                                viewDef.textureStates?.disabled ||
                                viewDef.textureStates?.out;
                            if (targetTextureData) {
                                const view = viewDef.view as Phaser.GameObjects.Image;
                                view.setTexture(targetTextureData.texture, targetTextureData.frame);
                            }
                            break;
                        case GameObjectType.TEXT:
                            targetTextData = 
                                viewDef.textStates?.disabled ||
                                viewDef.textStates.out;
                            if (targetTextData) {
                                const view = viewDef.view as Phaser.GameObjects.Text;
                                if (targetTextData) {
                                    if (targetTextData.metrics) {
                                        view.style.setStyle(targetTextData, false, false);
                                        view.style["metrics"] = targetTextData.metrics;
                                        view.updateText();
                                    }
                                    else {
                                        view.setStyle(targetTextData);
                                    }
                                }
                            }
                            break;
                        default:
                            console.warn("UpdateView; Unrecognized view type:", viewDef.view.type);
                    }
                });
                //#endregion
            }

        }
    }
}